import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import RolesTab from '../../photo-studio/permissions/tabs/roleTab';
import UsersTab from '../../photo-studio/permissions/tabs/usersTab';
import type { Role, User, Menu, CreateRoleDto, InviteUserDto } from '../../suit-shop/permissions/types';
import type { UpdateUserPayload } from '../../suit-shop/permissions/tabs/usersTab';
import {
  buildMenuQueryParams,
  extractMenuArray,
  getSelectedMenuContext,
  getUserVendorCategoryIdx,
  normalizeBoolean,
  normalizeMenuItems,
} from '../../../../../utils/menu';

const normalizeRoleType = (value: unknown, isAdmin: boolean): 'ADMIN' | 'GENERAL' => {
  if (value === 'ADMIN' || value === 'GENERAL') return value;
  return isAdmin ? 'ADMIN' : 'GENERAL';
};

const USER_INFO_UPDATED_EVENT = 'wedmin:user-info-updated';

export default function MakeupPermissions() {
  const [activeTab, setActiveTab] = useState<'roles' | 'users'>('roles');
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ loginId: string; tempPassword: string } | null>(null);

  const fetchMenus = useCallback(async () => {
    try {
      const userStr = localStorage.getItem('userInfo');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const categoryIdx = getUserVendorCategoryIdx(user);
      const selectedContext = getSelectedMenuContext(user);
      const token = localStorage.getItem('accessToken');
      const query = buildMenuQueryParams({
        vendorCategoryIdx: categoryIdx,
        isGuest: selectedContext?.isGuestContext ?? false
      });
      const response = await fetch(`https://api.wedmin.biz/menu${query}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      const menuRows = normalizeMenuItems(extractMenuArray(result));
      const mappedMenus: Menu[] = menuRows.map((m) => ({
        id: m.menuIdx,
        name: m.name,
        path: m.path || '',
        icon: m.icon || '',
        isActive: normalizeBoolean(m.isActive ?? true),
      }));
      setMenus(mappedMenus);

      if (mappedMenus.length === 0 && selectedContext?.menus) {
        const fallbackMenus: Menu[] = normalizeMenuItems(selectedContext.menus).map((m) => ({
          id: m.menuIdx,
          name: m.name,
          path: m.path || '',
          icon: m.icon || '',
          isActive: normalizeBoolean(m.isActive ?? true),
        }));
        setMenus(fallbackMenus);
      }
    } catch (error) {
      console.error('Fetch Menus Error:', error);
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/auth/permission/role', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.status) {
        const roleItems = Array.isArray(result.data) ? result.data : [];
        const mappedRoles: Role[] = roleItems.map((item: any) => {
          const isAdmin = normalizeBoolean(item.isAdmin ?? item.is_admin_role);
          return {
            roleIdx: Number(item.roleIdx),
            name: item.name || '',
            description: item.description || '',
            accessibleMenuIds: Array.isArray(item.accessibleMenuIds)
              ? item.accessibleMenuIds.map((id: unknown) => Number(id)).filter((id: number) => Number.isFinite(id))
              : Array.isArray(item.menuIds)
                ? item.menuIds.map((id: unknown) => Number(id)).filter((id: number) => Number.isFinite(id))
                : [],
            userCount: Number(item.userCount ?? 0),
            isAdmin,
            roleType: normalizeRoleType(item.roleType, isAdmin)
          };
        });
        setRoles(mappedRoles);
      }
    } catch (error) {
      console.error('Fetch Roles Error:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/user?size=100', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result?.data?.items) {
        const mappedUsers: User[] = result.data.items.map((item: any) => ({
          userIdx: item.userIdx,
          name: item.name || '',
          loginId: item.loginId || '',
          email: item.email || '',
          phone: item.phone || '',
          roleIdx: item.role?.roleIdx || item.roleIdx || null,
          roleName: item.role?.name || item.roleName || '',
          status: item.status || 'ACTIVE',
          isAdmin: normalizeBoolean(item.isAdmin),
        }));
        setUsers(mappedUsers);
      }
    } catch (error) {
      console.error('Fetch Users Error:', error);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([fetchMenus(), fetchRoles(), fetchUsers()]);
    setIsLoading(false);
  }, [fetchMenus, fetchRoles, fetchUsers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreateRole = async (data: CreateRoleDto) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/auth/permission/role', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (result.status) {
        alert('역할이 생성되었습니다.');
        fetchRoles();
      } else {
        alert(`생성 실패: ${result.message}`);
      }
    } catch (e) { console.error(e); alert('오류 발생'); }
  };

  const handleUpdateRole = async (roleIdx: number, data: Partial<CreateRoleDto>) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/auth/permission/role/${roleIdx}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (result.status) {
        alert('역할이 수정되었습니다.');
        fetchRoles();
        window.dispatchEvent(new Event(USER_INFO_UPDATED_EVENT));
      } else {
        alert(`수정 실패: ${result.message}`);
      }
    } catch (e) { console.error(e); alert('오류 발생'); }
  };

  const handleDeleteRole = async (roleIdx: number) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/auth/permission/role/${roleIdx}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.status) {
        alert('삭제되었습니다.');
        fetchRoles();
      } else {
        alert(`삭제 실패: ${result.message}`);
      }
    } catch (e) { console.error(e); alert('오류 발생'); }
  };

  const handleInviteUser = async (data: InviteUserDto) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/auth/invite', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (result.status) {
        setInviteResult(result.data);
        fetchUsers();
      } else {
        alert(`초대 실패: ${result.message}`);
      }
    } catch (e) { console.error(e); alert('오류 발생'); }
  };

  const handleUpdateUser = async (userIdx: number, data: UpdateUserPayload) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/user/${userIdx}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (result.status) {
        alert('수정되었습니다.');
        fetchUsers();
        window.dispatchEvent(new Event(USER_INFO_UPDATED_EVENT));
      } else {
        alert(`수정 실패: ${result.message}`);
      }
    } catch (e) { console.error(e); alert('오류 발생'); }
  };

  const handleDeleteUser = async (userIdx: number) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/user/${userIdx}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.status) {
        alert('삭제되었습니다.');
        fetchUsers();
      } else {
        alert(`삭제 실패: ${result.message}`);
      }
    } catch (e) { console.error(e); alert('오류 발생'); }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">사용자 관리</h1>
          <p className="text-sm text-gray-600 mt-1">메이크업샵 역할 및 사용자 권한을 관리합니다</p>
        </div>

        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button onClick={() => setActiveTab('roles')}
            className={`px-6 py-3 font-medium transition-colors cursor-pointer ${activeTab === 'roles' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-800'}`}>
            역할 관리
          </button>
          <button onClick={() => setActiveTab('users')}
            className={`px-6 py-3 font-medium transition-colors cursor-pointer ${activeTab === 'users' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-800'}`}>
            사용자 관리
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : activeTab === 'roles' ? (
          <RolesTab roles={roles} menus={menus} onCreate={handleCreateRole} onUpdate={handleUpdateRole} onDelete={handleDeleteRole} />
        ) : (
          <UsersTab
            users={users} roles={roles}
            onInvite={handleInviteUser} onUpdate={handleUpdateUser} onDelete={handleDeleteUser}
            inviteResult={inviteResult} onClearInviteResult={() => setInviteResult(null)}
          />
        )}
      </div>
    </div>
  );
}
